import { clientApi } from '@phoenix/clientApi';

declare const Phoenix: {
  toaster?: {
    notify: (payload: NotificationPayload) => void;
  };
};

declare global {
  interface Window {
    route: (name: string) => string;
  }
}

type NotificationPayload = {
  text: string;
  type: string;
};

export default class Notification {
  name: string;

  constructor(name = 'notifications') {
    this.name = name;

    document.addEventListener('DOMContentLoaded', () => {
      this.init();
    });
  }

  private getStoredNotifications(): NotificationPayload[] {
    const rawValue = window.localStorage.getItem(this.name);
    if (!rawValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) {
        throw new Error('Invalid notification shape');
      }
      return parsedValue;
    } catch (error) {
      console.error('Phoenix notifications parsing failed', error);
      window.localStorage.removeItem(this.name);
      return [];
    }
  }

  private persistNotifications(notifications: NotificationPayload[]) {
    if (!notifications.length) {
      window.localStorage.removeItem(this.name);
      return;
    }

    window.localStorage.setItem(this.name, JSON.stringify(notifications));
  }

  displayNotifications() {
    const notifications = this.getStoredNotifications();
    if (!notifications.length) {
      return;
    }

    notifications.forEach((notification) => {
      Phoenix.toaster?.notify({
        type: notification.type,
        text: notification.text,
      });
    });

    window.localStorage.removeItem(this.name);
  }

  create(text: string, type: string) {
    this.persistNotifications([{ text, type }]);
  }

  push(newNotifications: NotificationPayload[]) {
    const notifications = this.getStoredNotifications();
    notifications.push(...newNotifications);
    this.persistNotifications(notifications);
  }

  init(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const refreshParam = urlParams.get('refreshNotifications');

    if (refreshParam === '1' || refreshParam === 'true') {
      clientApi
        .get(window.route('api.notifications.pending'))
        .then(() => {
          this.displayNotifications();
        })
        .catch((error: unknown) => {
          console.error('[Notification] Error fetching notifications:', error);
        });
    } else {
      this.displayNotifications();
    }
  }
}
